import { Trans } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { BrandChip } from '../components/brand-chip.js';
import { useNotificationDrawer } from '../components/notification-drawer.js';
import { TopNav } from '../components/top-nav.js';
import { useActiveBrandId } from '../lib/active-brand.js';
import {
  archiveNotification,
  type Brand,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { brandsQuery, notificationsListQuery } from '../lib/queries.js';

// /notifications — cross-brand inbox. Phase 1d ships the working
// shell: list, mark-read on row click, mark-all-read, archive. Filter
// tabs (all / unread / archived) + per-brand scoping land in the
// next phase once the prefs ui is in place; until then this is the
// minimum viable inbox so the SSE + dispatch loop is actually
// exercise-able end to end.

export const Route = createFileRoute('/notifications')({
  loader: ({ context }) => context.queryClient.ensureQueryData(notificationsListQuery({})),
  component: NotificationsRoute,
});

function NotificationsRoute() {
  const { data, refetch, isFetching } = useQuery(notificationsListQuery({}));
  const queryClient = useQueryClient();

  const readMutation = useMutation({
    mutationFn: (id: string) => markNotificationRead(id, true),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
      ]);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveNotification(id, true),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
      ]);
    },
  });

  const readAllMutation = useMutation({
    mutationFn: () => markAllNotificationsRead('all'),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] }),
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] }),
      ]);
    },
  });

  const items = data?.notifications ?? [];
  const { data: brands = [] } = useQuery(brandsQuery());
  const brandsById = useMemo(() => {
    const m = new Map<string, Brand>();
    for (const b of brands) m.set(b.id, b);
    return m;
  }, [brands]);

  return (
    <>
      <TopNav active={null} />
      <main className="mx-auto max-w-3xl px-5 py-8">
        <header className="mb-6 flex items-end justify-between">
          <div>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
              {'↘'} <Trans>inbox</Trans>
            </p>
            <h1 className="mt-1 font-display text-[26px] font-bold tracking-[-0.02em] text-[var(--ink)]">
              <Trans>notifications</Trans>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <PreferencesLink />
            <button
              type="button"
              onClick={() => refetch()}
              className="btn btn-ghost text-[12px]"
              disabled={isFetching}
            >
              {'↻'} <Trans>refresh</Trans>
            </button>
            <button
              type="button"
              onClick={() => readAllMutation.mutate()}
              className="btn btn-ghost text-[12px]"
              disabled={readAllMutation.isPending}
            >
              <Trans>mark all read</Trans>
            </button>
          </div>
        </header>

        {items.length === 0 ? (
          <EmptyState />
        ) : (
          <NotificationList
            items={items}
            brandsById={brandsById}
            onMarkRead={(id) => readMutation.mutate(id)}
            onArchive={(id) => archiveMutation.mutate(id)}
          />
        )}
      </main>
    </>
  );
}

function NotificationList({
  items,
  brandsById,
  onMarkRead,
  onArchive,
}: {
  items: NotificationItem[];
  brandsById: Map<string, Brand>;
  onMarkRead: (id: string) => void;
  onArchive: (id: string) => void;
}) {
  const { openNotification } = useNotificationDrawer();
  return (
    <ul className="flex flex-col gap-2">
      {items.map((n) => (
        <li key={n.id}>
          <NotificationRow
            notification={n}
            brand={n.brandId ? (brandsById.get(n.brandId) ?? null) : null}
            onMarkRead={() => onMarkRead(n.id)}
            onArchive={() => onArchive(n.id)}
            onOpen={() => openNotification(n)}
          />
        </li>
      ))}
    </ul>
  );
}

interface RowProps {
  notification: NotificationItem;
  brand: Brand | null;
  onMarkRead: () => void;
  onArchive: () => void;
  onOpen: () => void;
}

function NotificationRow({ notification, brand, onMarkRead, onArchive, onOpen }: RowProps) {
  const unread = notification.readAt === null;
  const created = new Date(notification.createdAt);
  return (
    <div
      className={`flex items-start gap-3 rounded-lg border-[1.5px] ${
        unread ? 'border-[var(--ink)] bg-[var(--paper)]' : 'border-[var(--line)] bg-white'
      } shadow-[2px_2px_0_var(--line)]`}
    >
      {/* Whole-row click opens the global details drawer. Mark-read /
          archive are flex siblings (not nested) so they stay valid
          HTML and don't bubble into the row's open handler. */}
      <button type="button" onClick={onOpen} className="min-w-0 flex-1 p-4 text-left">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {unread ? (
            <span aria-hidden className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--pulse)]" />
          ) : null}
          <BrandChip brand={brand} fallbackName={notification.brandId ? '…' : 'magig'} />
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
            {notification.kind} · {relativeTime(created)}
          </p>
        </div>
        <h2 className="mt-1.5 font-display text-[15px] font-bold tracking-[-0.01em] text-[var(--ink)]">
          {notification.title}
        </h2>
        <p className="mt-1 line-clamp-2 whitespace-pre-wrap text-[13.5px] leading-snug text-[var(--ink)]/80">
          {notification.body}
        </p>
        {notification.cta ? (
          // Display-only — the row opens the drawer, which carries the
          // real CTA navigation. Mirrors the bell dropdown's row.
          <span className="group mt-2 inline-flex items-center gap-1 text-[12.5px] font-semibold text-[var(--plum)]">
            <span aria-hidden className="mag-arrow mag-arrow-ne">
              ↗
            </span>
            {notification.cta.label}
          </span>
        ) : null}
      </button>
      <div className="flex shrink-0 flex-col items-end gap-1 p-4 pl-0">
        {unread ? (
          <button type="button" onClick={onMarkRead} className="btn btn-ghost text-[11px]">
            <Trans>mark read</Trans>
          </button>
        ) : null}
        <button type="button" onClick={onArchive} className="btn btn-ghost text-[11px]">
          <Trans>archive</Trans>
        </button>
      </div>
    </div>
  );
}

function PreferencesLink() {
  // Notifications prefs live inside per-brand settings now (s178.7d).
  // Route to whichever brand is active; fall back to /brands when
  // none is selected so the founder picks one explicitly.
  const { data: brands = [] } = useQuery(brandsQuery());
  const activeBrandId = useActiveBrandId();
  const live = brands.filter((b) => b.archivedAt === null);
  const target = live.find((b) => b.id === activeBrandId) ?? live[0];
  if (!target) {
    return (
      <Link to="/brands" className="btn btn-ghost text-[12px] no-underline">
        <Trans>preferences</Trans>
      </Link>
    );
  }
  return (
    <Link
      to="/brand/$slug/settings"
      params={{ slug: target.slug }}
      className="btn btn-ghost text-[12px] no-underline"
    >
      <Trans>preferences</Trans>
    </Link>
  );
}

function EmptyState() {
  return (
    <div className="rounded-lg border border-dashed border-[var(--line)] bg-white p-10 text-center">
      <p className="font-display text-[15px] text-[var(--ink)]/70">
        <Trans>nothing here yet.</Trans>
      </p>
      <p className="mt-1 text-[12.5px] text-[var(--ink)]/50">
        <Trans>i'll send things here when there's news worth your time.</Trans>
      </p>
    </div>
  );
}

function relativeTime(d: Date): string {
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d ago`;
  // Explicit numeric shape: this sits at the end of a run of terse
  // relative chips ("3h ago", "5d ago") where the bare toLocaleDateString
  // used to give the locale's short numeric form. The helper's default is
  // a month NAME, which reads long here.
  return formatDate(d, { year: 'numeric', month: 'numeric', day: 'numeric' });
}
